import { api } from '@packages/backend/convex/_generated/api';
import { type PushNotificationStatus, zPushNotificationStatus } from '@packages/shared';
import { createFileRoute, Link } from '@tanstack/react-router';
import { useQuery } from 'convex-helpers/react';
import { format } from 'date-fns';
import { ActionButton } from '@/components/ActionButton';
import { ColorBadge } from '@/components/ColorBadge';
import { ErrorAlert } from '@/components/ErrorAlert';
import { H3 } from '@/components/H3';
import { LoadingIcon } from '@/components/LoadingIcon';
import { Card, CardAction, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';

export const Route = createFileRoute('/a/push-notifications/$notificationId')({
  validateSearch: (search) => ({
    status: zPushNotificationStatus.default('pending').parse(search.status),
  }),
  component: PushNotificationDetailsPage,
});

function PushNotificationDetailsPage() {
  const { notificationId } = Route.useParams();
  const search = Route.useSearch();

  const { data, error, isError, isPending } = useQuery(api.pushNotifications.getById, {
    id: notificationId,
  });

  if (isPending) {
    return <LoadingIcon />;
  }

  if (isError) {
    return <ErrorAlert message={error.message} title="Error loading notification" />;
  }

  if (data === null) return null; // Signed out

  const { notification, recipient } = data;

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center gap-2">
          <CardTitle>Push Notification</CardTitle>
          <ColorBadge
            color={
              notification.status === 'sent'
                ? 'green'
                : notification.status === 'pending'
                  ? 'yellow'
                  : notification.status === 'error'
                    ? 'red'
                    : 'gray'
            }
          >
            {notification.status.toUpperCase()}
          </ColorBadge>
        </div>
        <CardAction>
          <ActionButton asChild variant="outline">
            <Link to="/a/push-notifications" search={{ status: search.status }}>
              Back to List
            </Link>
          </ActionButton>
        </CardAction>
      </CardHeader>
      <CardContent>
        {/* Push Notification Preview */}
        <div className="mb-6 flex flex-col items-center">
          <div
            className={`
                w-full max-w-md rounded-xl shadow-md px-6 py-4
                ${getStatusColor(notification.status)} 
                border border-gray-200
              `}
          >
            <div className="mt-2">
              <div className="font-bold text-lg text-gray-900">
                {notification.expoPushNotification.title || '-'}
              </div>
              <div className="text-gray-800 mt-1">
                {notification.expoPushNotification.body || '-'}
              </div>
            </div>
          </div>
        </div>

        {/* Recipient Details */}
        <div className="mb-8">
          <Separator className="my-6" />
          <H3>Recipient Details</H3>
          {recipient.restaurantUser ? (
            <div className="mt-4 rounded-lg border bg-muted/50 p-4">
              <div className="flex items-center gap-2 mb-2">
                <ColorBadge color="blue">Restaurant User</ColorBadge>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-2">
                <div>
                  <Label>Name</Label>
                  <div className="text-base">{recipient.restaurantUser.name}</div>
                </div>
                <div>
                  <Label>Restaurant ID</Label>
                  <div className="text-base break-all">
                    {(recipient.restaurantUser?.restaurantIds ?? []).map(
                      (id: string, idx: number, arr: string[]) => (
                        <span key={id}>
                          <Link
                            to="/r/$restaurantId/s/users"
                            params={{ restaurantId: id }}
                            className="text-blue-600 hover:underline text-sm"
                          >
                            {id}
                          </Link>
                          {idx < arr.length - 1 && ', '}
                        </span>
                      ),
                    )}
                  </div>
                </div>
              </div>
            </div>
          ) : recipient.restaurantCustomer ? (
            <div className="mt-4 rounded-lg border bg-muted/50 p-4">
              <div className="flex items-center gap-2 mb-2">
                <ColorBadge color="green">Restaurant Customer</ColorBadge>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-2">
                <div>
                  <Label>Name</Label>
                  <div className="text-base">{recipient.restaurantCustomer.name || '-'}</div>
                </div>
                <div>
                  <Label>Restaurant ID</Label>
                  <div className="text-base break-all">
                    <Link
                      to="/r/$restaurantId/s/users"
                      params={{ restaurantId: recipient.restaurantCustomer.restaurantId }}
                      className="text-blue-600 hover:underline"
                    >
                      {recipient.restaurantCustomer.restaurantId}
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="mt-4">
              <ErrorAlert
                message="Recipient not found. This push notification is not associated with a restaurant user or customer."
                title="Recipient Not Found"
              />
            </div>
          )}
        </div>

        {/* Details */}
        <div className="space-y-4">
          <div>
            <h3 className="text-sm font-medium text-gray-500">Created At</h3>
            <p className="mt-1">{format(notification._creationTime, 'yyyy-MM-dd HH:mm:ss')}</p>
          </div>
          {notification.acceptedAt && (
            <div>
              <h3 className="text-sm font-medium text-gray-500">Accepted At</h3>
              <p className="mt-1">{format(notification.acceptedAt, 'yyyy-MM-dd HH:mm:ss')}</p>
            </div>
          )}
          {notification.sentAt && (
            <div>
              <h3 className="text-sm font-medium text-gray-500">Sent At</h3>
              <p className="mt-1">{format(notification.sentAt, 'yyyy-MM-dd HH:mm:ss')}</p>
            </div>
          )}
          {notification.errorAt && (
            <div>
              <h3 className="text-sm font-medium text-gray-500">Error At</h3>
              <p className="mt-1">{format(notification.errorAt, 'yyyy-MM-dd HH:mm:ss')}</p>
            </div>
          )}
          {notification.error && (
            <div>
              <h3 className="text-sm font-medium text-gray-500">Error Message</h3>
              <p className="mt-1 text-red-500">{notification.error}</p>
            </div>
          )}
          {notification.errorMessage && (
            <div>
              <h3 className="text-sm font-medium text-gray-500">Error Details</h3>
              <p className="mt-1 text-red-500">{notification.errorMessage}</p>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

function getStatusColor(status: PushNotificationStatus) {
  switch (status) {
    case 'sent':
      return 'bg-green-100 text-green-800';
    case 'pending':
      return 'bg-yellow-100 text-yellow-800';
    case 'error':
      return 'bg-red-100 text-red-800';
    default:
      return 'bg-gray-100 text-gray-800';
  }
}
